/**
 * v2.56 — PriceJobDetail component.
 *
 * Prop: jobId — polls getPriceJob every 2000ms while state is queued|running|deferring;
 * stops polling when state is done|failed or on unmount.
 */
import { useEffect, useRef, useState } from 'react';
import {
  Badge,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Text,
  Title,
} from '@tremor/react';
import { getPriceJob, priceAuditCsvUrl } from '../api/priceUpload';
import type { PriceJobDetail as PriceJobDetailType } from '../api/types';

const POLLING_STATES = new Set(['queued', 'running', 'deferring']);

function stateColor(state: string): 'gray' | 'yellow' | 'blue' | 'green' | 'red' {
  switch (state) {
    case 'done':      return 'green';
    case 'failed':    return 'red';
    case 'running':   return 'blue';
    case 'deferring': return 'yellow';
    case 'queued':    return 'gray';
    default:          return 'gray';
  }
}

interface Props {
  jobId: string;
}

export default function PriceJobDetail({ jobId }: Props) {
  const [job, setJob] = useState<PriceJobDetailType | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;

    // Fetch immediately, then poll while state is non-terminal
    let intervalId: ReturnType<typeof setInterval> | null = null;

    async function fetchJob() {
      try {
        const data = await getPriceJob(jobId);
        if (!mounted.current) return;
        setJob(data);
        // If state is terminal, clear the interval
        if (!POLLING_STATES.has(data.state) && intervalId !== null) {
          clearInterval(intervalId);
          intervalId = null;
        }
      } catch (e) {
        if (mounted.current) setErr((e as Error).message ?? String(e));
        if (intervalId !== null) {
          clearInterval(intervalId);
          intervalId = null;
        }
      }
    }

    fetchJob();
    intervalId = setInterval(fetchJob, 2000);

    return () => {
      mounted.current = false;
      if (intervalId !== null) clearInterval(intervalId);
    };
  }, [jobId]);

  if (!job && !err) {
    return <Text className="text-tremor-content text-sm">Cargando…</Text>;
  }

  if (err) {
    return <Text className="text-red-600 dark:text-red-400 text-sm">{err}</Text>;
  }

  if (!job) return null;

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card>
        <div className="flex items-center gap-3">
          <Title className="text-base font-mono">{job.id}</Title>
          <Badge color={stateColor(job.state)}>{job.state}</Badge>
        </div>
        <Text className="text-xs text-tremor-content mt-1">
          Creado por {job.created_by} · {new Date(job.created_at).toLocaleString('es-MX')}
        </Text>

        <div className="mt-3">
          <a
            href={priceAuditCsvUrl(job.id)}
            className="text-blue-600 dark:text-blue-400 text-sm underline"
            download
          >
            Descargar auditoría (CSV)
          </a>
        </div>
      </Card>

      {/* Chunks table */}
      <Card>
        <Title className="text-base mb-2">Chunks</Title>
        {job.chunks.length === 0 ? (
          <Text className="text-tremor-content text-sm italic">Sin chunks</Text>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>#</TableHeaderCell>
                  <TableHeaderCell>Línea</TableHeaderCell>
                  <TableHeaderCell>Partes</TableHeaderCell>
                  <TableHeaderCell>Fase</TableHeaderCell>
                  <TableHeaderCell className="text-right">OK</TableHeaderCell>
                  <TableHeaderCell className="text-right">Creados</TableHeaderCell>
                  <TableHeaderCell className="text-right">Diferidos</TableHeaderCell>
                  <TableHeaderCell className="text-right">Errores</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {job.chunks.map((chunk) => {
                  const results = chunk.results ?? [];
                  const ok       = results.filter((r) => r.status === 'ok').length;
                  const created  = results.filter((r) => r.status === 'created').length;
                  const deferred = results.filter((r) => r.status === 'deferred').length;
                  const errored  = results.filter((r) => r.status === 'errored').length;
                  const partRange =
                    chunk.parts.length === 0
                      ? '—'
                      : `${chunk.parts[0]}–${chunk.parts[chunk.parts.length - 1]}`;

                  return (
                    <TableRow key={chunk.id}>
                      <TableCell className="tabular-nums">{chunk.id}</TableCell>
                      <TableCell>{chunk.line}</TableCell>
                      <TableCell className="font-mono text-sm">{partRange}</TableCell>
                      <TableCell>{chunk.phase}</TableCell>
                      <TableCell className="text-right tabular-nums">{ok}</TableCell>
                      <TableCell className="text-right tabular-nums">{created}</TableCell>
                      <TableCell className="text-right tabular-nums">{deferred}</TableCell>
                      <TableCell className="text-right tabular-nums">{errored}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
